import React, { useEffect, useMemo, useRef, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'
import DateRange from '../components/DateRange.jsx'
import NoteDrawer from '../components/NoteDrawer.jsx'
import JournalEditor from '../components/JournalEditor.jsx'
import VendorPickerModal from '../components/VendorPickerModal.jsx'
import FilterPopover from '../components/FilterPopover.jsx'

// 이름을 granter 에서 fa 로 옮기면서 localStorage 키도 바꿨다. 그냥 바꾸면 사람들이
// 골라 둔 통화 탭과 계좌가 초기화되므로, 새 키가 비어 있을 때만 옛 키를 한 번 읽어 온다.
const 기억 = {
  get(key) {
    try {
      const v = localStorage.getItem(`fa-${key}`)
      if (v !== null) return v
      const 옛 = localStorage.getItem(`granter-${key}`)
      if (옛 !== null) localStorage.setItem(`fa-${key}`, 옛)
      return 옛
    } catch { return null }
  },
  set(key, value) {
    try { localStorage.setItem(`fa-${key}`, value) } catch {}
  },
}

const SCREEN_KEY = { bank: 'data_bank', tax: 'data_tax', card: 'data_card', securities: 'data_securities' }

// 표를 두 영역으로 나눈다. 왼쪽은 은행·카드가 준 그대로이고 오른쪽은 우리가 붙인 값이다.
// 섞어 두면 어디까지가 원천이고 어디부터가 우리 판단인지 알 수 없다.
const KIND_TITLE = { bank: '통장', card: '카드', tax: '세금계산서', securities: '증권' }

const 분류열 = new Set(['부서명', '계정과목명', '자금항목명', '위하고거래처명',
                        '전표상태', '전표담당자', '분개', '세목'])

// 기간 단추. 자금은 늘 '이번 달'과 '지난 분기'를 오가므로 손이 자주 가는 자리다.
const 기간버튼 = [
  { k: 'day', label: '일' }, { k: 'week', label: '주' }, { k: 'month', label: '월' },
  { k: 'quarter', label: '분기' }, { k: 'year', label: '연' },
]

function 기간계산(k, 기준 = new Date()) {
  const d = new Date(기준)
  const iso = (x) => new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
  if (k === 'day') return [iso(d), iso(d)]
  if (k === 'week') {
    const s = new Date(d); s.setDate(d.getDate() - ((d.getDay() + 6) % 7))
    const e = new Date(s); e.setDate(s.getDate() + 6)
    return [iso(s), iso(e)]
  }
  if (k === 'month') return [iso(new Date(d.getFullYear(), d.getMonth(), 1)),
                             iso(new Date(d.getFullYear(), d.getMonth() + 1, 0))]
  if (k === 'quarter') {
    const q = Math.floor(d.getMonth() / 3)
    return [iso(new Date(d.getFullYear(), q * 3, 1)), iso(new Date(d.getFullYear(), q * 3 + 3, 0))]
  }
  return [iso(new Date(d.getFullYear(), 0, 1)), iso(new Date(d.getFullYear(), 11, 31))]
}


// 데이터 화면 공통 (계좌/세금계산서/신용카드/증권)
// 조회·검색·합계 + 계정과목/적요 수정 + 위하고 거래처 매칭 + 전표 승인요청
const KIND_CONFIG = {
  bank: {
    title: '계좌', hasDesc: true, hasDept: true, cpField: '상대입출금자명',
    cols: [
      { k: '일자', align: 'c' }, { k: '시각', align: 'c' },
      { k: '계좌은행', label: '은행', align: 'c', bank: true }, { k: '계좌번호', label: '계좌', align: 'c', acct: true },
      { k: '적요', align: 'l', desc: true }, { k: '상대입출금자명', align: 'l' },
      { k: '입금액', num: true }, { k: '출금액', num: true },
      { k: '부서명', label: '부서', align: 'l', dept: true },
      { k: '계정과목명', align: 'l', cat: true },
      { k: '자금항목명', label: '자금항목', align: 'l', fund: true },
      { k: '위하고거래처명', align: 'l', vendor: true },
      { k: '전표상태', align: 'c', badge: true }, { k: '전표담당자', align: 'c' },
      { k: '분개', align: 'c', journal: true },
    ],
    // 외화계좌 탭 - 원화 탭 항목은 그대로 두고 통화/외화금액/환율을 앞에 끼워넣는다.
    // 환율은 수집이 받아온 은행 환산가(원화÷외화)라 회계용 매매기준율과는 다르다.
    fxCols: [
      { k: '일자', align: 'c' }, { k: '시각', align: 'c' },
      { k: '계좌은행', label: '은행', align: 'c', bank: true }, { k: '계좌번호', label: '계좌', align: 'c', acct: true },
      { k: '통화', align: 'c' },
      { k: '적요', align: 'l', desc: true }, { k: '상대입출금자명', align: 'l' },
      { k: '입금액(외화)', num: true, dec: 2, fx: true },
      { k: '출금액(외화)', num: true, dec: 2, fx: true },
      { k: '잔액(외화)', num: true, dec: 2, fx: true, noSum: true },
      { k: '적용환율', num: true, dec: 2, noSum: true },
      { k: '입금액', label: '입금액(원화)', num: true },
      { k: '출금액', label: '출금액(원화)', num: true },
      { k: '부서명', label: '부서', align: 'l', dept: true },
      { k: '계정과목명', align: 'l', cat: true },
      { k: '자금항목명', label: '자금항목', align: 'l', fund: true },
      { k: '위하고거래처명', align: 'l', vendor: true },
      { k: '전표상태', align: 'c', badge: true }, { k: '전표담당자', align: 'c' },
      { k: '분개', align: 'c', journal: true },
    ],
  },
  tax: {
    title: '세금계산서', hasDesc: false, cpField: '거래처명',
    cols: [
      { k: '일자', align: 'c' }, { k: '구분', align: 'c' },
      { k: '거래처명', align: 'l' }, { k: '사업자등록번호', align: 'c' },
      { k: '공급가액', num: true }, { k: '부가세', num: true }, { k: '합계', num: true },
      { k: '계정과목', align: 'l', cat: true }, { k: '위하고거래처명', align: 'l', vendor: true },
      { k: '전표상태', align: 'c', badge: true },
    ],
  },
  card: {
    // 적요는 카드 티켓에 없는 필드라 우리 DB에만 담긴다(server.SOURCE_HAS_DESC).
    title: '신용카드', hasDesc: true, hasDept: true, cpField: '거래처명',
    cols: [
      { k: '일자', align: 'c' },
      { k: '거래처명', align: 'l' },
      { k: '카드별칭', label: '카드', align: 'l' },
      { k: '카드사명', align: 'l' }, { k: '카드번호', align: 'c' },
      { k: '공급가액', num: true }, { k: '부가세', num: true }, { k: '합계', num: true },
      { k: '적요', align: 'l', desc: true }, { k: '부서명', label: '부서', align: 'l', dept: true },
      { k: '계정과목', align: 'l', cat: true },
      { k: '자금항목명', label: '자금항목', align: 'l', fund: true },
      { k: '위하고거래처명', align: 'l', vendor: true },
      { k: '전표상태', align: 'c', badge: true },
    ],
  },
  securities: {
    title: '증권', hasDesc: true, cpField: '상대입출금자명',
    // 원화(MMT·예수금)와 외화(RP·환전)는 자릿수가 달라 섞어 보면 읽기 어렵다.
    cols: [
      { k: '일자', align: 'c' }, { k: '우리계좌', align: 'l' },
      { k: '적요', align: 'l', desc: true }, { k: '상대입출금자명', align: 'l' },
      { k: '입금액', num: true }, { k: '출금액', num: true }, { k: '잔액', num: true },
      { k: '이자수익_거래통화', label: '이자(과표)', num: true },
      { k: '소득세_원', label: '소득세', num: true },
      { k: '지방소득세_원', label: '지방소득세', num: true },
      { k: '계정과목명', align: 'l', cat: true },
      { k: '전표상태', align: 'c', badge: true },
    ],
    // 외화는 소수점 두 자리까지 본다
    fxCols: [
      { k: '일자', align: 'c' }, { k: '우리계좌', align: 'l' }, { k: '통화', align: 'c' },
      { k: '적요', align: 'l', desc: true }, { k: '상대입출금자명', align: 'l' },
      { k: '입금액', label: '입금액(외화)', num: true, dec: 2 },
      { k: '출금액', label: '출금액(외화)', num: true, dec: 2 },
      { k: '잔액', label: '잔액(외화)', num: true, dec: 2 },
      { k: '환율', num: true, dec: 2, noSum: true },
      { k: '원화환산액', num: true },
      { k: '이자수익_거래통화', label: '이자(외화)', num: true, dec: 2 },
      { k: '소득세_원', label: '소득세', num: true },
      { k: '지방소득세_원', label: '지방소득세', num: true },
      { k: '계정과목명', align: 'l', cat: true },
      { k: '전표상태', align: 'c', badge: true },
    ],
  },
}

const STATUS_LABEL = { none: '', requested: '요청중', approved: '승인', rejected: '반려' }

// 은행명 축약 — 서버 bank_style.BANK_SHORT와 같은 표.
// 계좌 화면의 '우리계좌'는 "카페24 (기업은행 51905605004017)"처럼 길어서 가로 스크롤을
// 잡아먹는다. 은행 기업 / 계좌 4017 두 열로 나눠 보여준다.
const BANK_SHORT = {
  국민은행: '국민', 기업은행: '기업', 농협은행: '농협', 신한은행: '신한',
  우리은행: '우리', 하나은행: '하나', 새마을금고: '새마을', 카카오뱅크: '카카오',
  토스뱅크: '토스', SC제일은행: 'SC제일', 우체국: '우체국', 케이뱅크: '케이',
  부산은행: '부산', 대구은행: '대구', iM뱅크: 'iM', 경남은행: '경남',
  광주은행: '광주', 전북은행: '전북', 제주은행: '제주', 수협은행: '수협', 씨티은행: '씨티',
}
const bankShort = (name) => BANK_SHORT[name] || (name || '').replace('은행', '').slice(0, 3) || '?'
const acctLast4 = (no) => { const s = String(no || ''); return s.length > 4 ? s.slice(-4) : s }
const CAT_ID = { bank: '계정과목ID', securities: '계정과목ID', tax: '계정과목ID', card: '계정과목ID' }
const CAT_NAME = { bank: '계정과목명', securities: '계정과목명', tax: '계정과목', card: '계정과목' }

const monthRange = (month) => {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  return [`${month}-01`, `${month}-${String(last).padStart(2, '0')}`]
}
const isLocked = (r) => (r['전표상태'] || 'none') !== 'none'

// 거래처 매칭 셀: 클릭 없이 바로 입력, 입력 중 실시간 후보 + F2로 전체 검색 팝업
function VendorCell({ row, kind, cpField, onSaved }) {
  const [q, setQ] = useState(row['위하고거래처명'] || '')
  const [sugs, setSugs] = useState([])
  const [focused, setFocused] = useState(false)
  const [showPicker, setShowPicker] = useState(false)

  useEffect(() => { setQ(row['위하고거래처명'] || '') }, [row['위하고거래처명']])

  useEffect(() => {
    if (!focused || q.trim().length < 1) { setSugs([]); return }
    const t = setTimeout(() => {
      portal.get(`/api/vendors/search?q=${encodeURIComponent(q.trim())}&kind=general`)
        .then(setSugs).catch(() => setSugs([]))
    }, 250)
    return () => clearTimeout(t)
  }, [q, focused])

  const save = async (v, vendorKind = 'general') => {
    await portal.post('/api/vendor-match', {
      kind, ticketId: row.ticket_id,
      vendorCode: v?.code ?? null, vendorName: v?.name ?? null,
      vendorKind, counterparty: row[cpField] || null,
    })
    setSugs([]); setFocused(false); setShowPicker(false)
    onSaved()
  }

  if (isLocked(row)) {
    return <span className={row['위하고거래처명'] ? '' : 'muted'}>{row['위하고거래처명'] || '미매칭'}</span>
  }

  return (
    <span className="vendor-edit">
      <input
        placeholder="미매칭 · F2 검색"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'F2') { e.preventDefault(); setShowPicker(true) }
          else if (e.key === 'Escape') { setQ(row['위하고거래처명'] || ''); e.currentTarget.blur() }
        }}
      />
      {row['위하고거래처명'] && (
        <button type="button" className="btn danger" title="매칭 해제" onClick={() => save(null)}>해제</button>
      )}
      <button type="button" className="btn secondary" title="전체 거래처에서 검색 (F2)" onClick={() => setShowPicker(true)}>🔍</button>
      {focused && sugs.length > 0 && (
        <span className="vendor-sugs">
          {sugs.slice(0, 8).map((v) => (
            <button key={v.code} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => save(v)}>
              {v.name} <i>{v.code}</i>
            </button>
          ))}
        </span>
      )}
      {showPicker && (
        <VendorPickerModal
          initialQuery={q}
          onSelect={(v) => save(v, v.kind)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </span>
  )
}

export default function SourceData({ kind, month, currentUserId, autoOpenNoteId, onAutoOpened }) {
  // 계좌 화면만 원화/외화 탭으로 갈라서 본다 (외화는 컬럼 구성이 다름)
  const [ccyTab, setCcyTab] = useState(() => {
    return 기억.get('bank-ccy') === 'fx' ? 'fx' : 'krw'
  })
  // 계좌와 증권은 원화/외화 탭으로 갈라 본다 (컬럼 구성과 자릿수가 다르다)
  const hasCcyTabs = kind === 'bank' || kind === 'securities'
  const cfg = useMemo(() => {
    const base = KIND_CONFIG[kind]
    return hasCcyTabs && ccyTab === 'fx' && base.fxCols ? { ...base, cols: base.fxCols } : base
  }, [kind, ccyTab, hasCcyTabs])
  const [defStart, defEnd] = monthRange(month)
  const [start, setStart] = useState(defStart)
  const [end, setEnd] = useState(defEnd)
  const [q, setQ] = useState('')
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [cats, setCats] = useState([])
  const [subsByCode, setSubsByCode] = useState({})
  const [depts, setDepts] = useState([])
  const [funds, setFunds] = useState([])
  const [editRows, setEditRows] = useState({}) // ticket_id -> {catId?, catName?, desc?}
  const [sel, setSel] = useState(new Set())
  const [users, setUsers] = useState([])
  const [approverId, setApproverId] = useState('')
  const [memo, setMemo] = useState('')
  const [toast, setToast] = useState('')
  const [lastSynced, setLastSynced] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const autoSyncedFor = useRef('')
  const [acctFilter, setAcctFilter] = useState('')
  const [flowFilter, setFlowFilter] = useState('')
  const [catPick, setCatPick] = useState(new Set())      // 계정과목 다중선택
  const [deptPick, setDeptPick] = useState(new Set())    // 부서 다중선택
  const [vendorPick, setVendorPick] = useState(new Set())// 거래처 다중선택
  const [quick, setQuick] = useState('')                 // 빠른 필터: 계정없음·거래처없음·부서없음
  const [hidden, setHidden] = useState(() => {           // 감춘 열. 사람마다 보는 것이 다르다
    try { return new Set(JSON.parse(기억.get(`cols-${kind}`) || '[]')) } catch { return new Set() }
  })
  const [colsOpen, setColsOpen] = useState(false)
  const [bulkDesc, setBulkDesc] = useState('')
  const [bulkCatId, setBulkCatId] = useState('')
  const [bulkDeptCode, setBulkDeptCode] = useState('')
  const [bulkFundId, setBulkFundId] = useState('')
  const [bulkVendor, setBulkVendor] = useState(null)
  const [bulkVendorOpen, setBulkVendorOpen] = useState(false)
  const [journalFor, setJournalFor] = useState(null) // 분개 편집기를 띄운 ticket_id


  const isBank = kind === 'bank'
  const [bankAccounts, setBankAccounts] = useState(null) // 계좌 화면 전용 - 계좌 목록(가벼움, 전체 조회 없이)
  const [selectedAsset, setSelectedAsset] = useState(undefined) // undefined=아직 결정 안 됨, 'ALL'|assetId

  const hasAccount = cfg.cols.some((c) => c.k === '우리계좌' || c.bank)
  const hasFlow = cfg.cols.some((c) => c.k === '입금액') && cfg.cols.some((c) => c.k === '출금액')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3000) }

  useEffect(() => { const [s, e] = monthRange(month); setStart(s); setEnd(e) }, [month])
  useEffect(() => { setAcctFilter(''); setFlowFilter(''); setBulkDesc(''); setBulkCatId('') }, [kind])

  // 계좌 화면 — 계좌 목록만 가볍게 받아서, 매번 전체계좌(수천 건)를 다 조회하지 않고
  // 기본으로 계좌 1개만 골라 보여준다. 어차피 계정과목/거래처 매칭도 계좌별로 하니까.
  useEffect(() => {
    if (!isBank) { setBankAccounts(null); setSelectedAsset(undefined); return }
    setBankAccounts(null)
    portal.get('/api/fa/bank/accounts')
      .then(setBankAccounts)
      .catch(() => setBankAccounts([]))
  }, [isBank])

  // 외화계좌는 통화별로 assetId가 따로라(신한 180012989970 → USD/CNY/AUD) 탭으로 갈리면
  // 선택기 목록도 갈린다 - 탭마다 마지막에 본 계좌를 따로 기억한다.
  const acctKey = `bank-account${ccyTab === 'fx' ? '-fx' : ''}`
  const visibleAccounts = useMemo(
    () => (bankAccounts ? bankAccounts.filter((a) => (ccyTab === 'fx' ? a.isForeign : !a.isForeign)) : null),
    [bankAccounts, ccyTab],
  )

  useEffect(() => {
    if (!isBank || !visibleAccounts) return
    let saved = null
    saved = 기억.get(acctKey)
    // 전체계좌는 기억하지 않는다. 한 달치 전 계좌를 끌어오면 응답이 백수십 KB라 화면이
    // 한참 비어 있는데, 그걸 고른 적이 있다는 이유로 다음에 열 때마다 다시 기다리게 된다.
    // 그 자리에서 고르면 그 세션에는 적용되고, 다시 열면 계좌 하나로 돌아온다.
    if (saved && saved !== 'ALL' && visibleAccounts.some((a) => String(a.assetId) === saved)) setSelectedAsset(saved)
    else setSelectedAsset(visibleAccounts.length ? String(visibleAccounts[0].assetId) : 'ALL')
  }, [isBank, visibleAccounts, acctKey])

  const changeAccount = (assetId) => {
    setSelectedAsset(assetId)
    기억.set(acctKey, assetId)
  }

  const changeCcyTab = (t) => {
    setCcyTab(t); setSel(new Set()); setEditRows({}); setAcctFilter(''); setFlowFilter('')
    기억.set('bank-ccy', t)
  }

  useEffect(() => {
    portal.get('/api/fa/meta/categories').then(setCats).catch(() => {})
    // 세목은 계정과목 아래 3차다. 쓰는 것만 담아 둔다.
    portal.get('/api/meta/subitems').then((d) => {
      const m = {}
      for (const a of d.accounts || []) {
        const on = (a.subitems || []).filter((x) => x.used)
        if (on.length) m[a.code] = on
      }
      setSubsByCode(m)
    }).catch(() => {})
    portal.get('/api/fa/meta/departments').then(setDepts).catch(() => {})
    portal.get('/api/fund-plan/items').then(setFunds).catch(() => {})
    portal.get('/api/auth/users-brief').then(setUsers).catch(() => {})
    portal.get('/api/voucher/settings')
      .then((s) => s.defaultApproverId && setApproverId(String(s.defaultApproverId)))
      .catch(() => {})
  }, [])

  // 화면은 캐시 DB에서 즉시 읽고, 오래됐으면 자동으로 수집 동기화 (증권은 자체 DB)
  const isCached = kind !== 'securities'
  const assetQS = isBank && selectedAsset && selectedAsset !== 'ALL' ? `&assetId=${encodeURIComponent(selectedAsset)}` : ''

  const doSync = async () => {
    setSyncing(true)
    try {
      const r = await portal.post(`/api/fa/${kind}/sync`, { start, end })
      setLastSynced(r.lastSynced)
      const c = await portal.get(`/api/fa/${kind}/cached?start=${start}&end=${end}${assetQS}`)
      setRows(c.records ?? [])
      notify(`동기화 완료 (${fmt(r.count)}건)`)
    } catch (e) { setErr(e.message) } finally { setSyncing(false) }
  }

  const load = () => {
    if (isBank && selectedAsset === undefined) return // 계좌 목록 결정 전에는 조회하지 않음
    setErr(''); setLoading(true)
    const req = isCached
      ? portal.get(`/api/fa/${kind}/cached?start=${start}&end=${end}${assetQS}`)
      : portal.get(`/api/fa/${kind}?start=${start}&end=${end}`)
    req
      .then((r) => {
        const records = isCached ? (r.records ?? []) : (Array.isArray(r) ? r : [])
        setRows(records); setEditRows({}); setSel(new Set())
        if (isCached) {
          setLastSynced(r.lastSynced)
          // 동기화는 매일 오전 9시 자동 스케줄 + '동기화' 수동 버튼으로만 실행.
          // 캐시가 아예 비어있는 경우(최초 조회 등)에만 안전망으로 1회 자동 동기화.
          const key = `${kind}|${start}|${end}`
          if (!r.lastSynced && autoSyncedFor.current !== key) {
            autoSyncedFor.current = key
            doSync()
          }
        }
      })
      .catch((e) => setErr(e.status === 403 ? `${cfg.title} 화면 권한이 없습니다.` : e.message))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [kind, start, end, selectedAsset])

  const 후보 = useMemo(() => {
    const 모으기 = (키) => [...new Set((rows || []).map((r) => r[키]).filter(Boolean))].sort()
    return {
      cat: 모으기(CAT_NAME[kind]),
      dept: 모으기('부서명'),
      vendor: 모으기('위하고거래처명'),
    }
  }, [rows, kind])

  // 빠른 필터에 붙는 건수. 숫자가 있어야 어디를 손봐야 하는지 바로 보인다.
  const 빈칸수 = useMemo(() => {
    const n = (조건) => (rows || []).filter(조건).length
    return {
      cat: n((r) => !r[CAT_NAME[kind]]),
      vendor: n((r) => !r['위하고거래처명']),
      dept: n((r) => !r['부서명']),
    }
  }, [rows, kind])

  const accountOptions = useMemo(() => {
    if (!rows || !hasAccount) return []
    return [...new Set(rows.map((r) => r['우리계좌']).filter(Boolean))].sort()
  }, [rows, hasAccount])

  // 위하고 계정과목표 순서(대분류>중분류) 그대로 묶어서 보여준다.
  //
  // 고르는 것은 계정과목이 아니라 '가장 구체적인 것'이다 - 세목이 있는 계정은 세목이,
  // 없는 계정은 계정 자체가 선택지가 된다. 세목을 고르면 계정과목이 따라온다. 부서를
  // 고르면 사업부가 따라오는 것과 같은 설계다. 둘을 따로 고르게 두면 '중식식대인데
  // 접대비' 같은 어긋남이 생기는데, 그건 나중에 손익을 볼 때야 드러난다.
  const catGroups = useMemo(() => {
    const groups = []
    let cur = null
    for (const c of cats) {
      const label = c.minor ? `${c.major} > ${c.minor}` : c.major
      if (!cur || cur.label !== label) { cur = { label, items: [] }; groups.push(cur) }
      const subs = subsByCode[c.code] || []
      if (subs.length) {
        for (const s of subs) {
          cur.items.push({ value: `${c.code}|${s.id}`, label: `${c.name} · ${s.name}`,
            code: c.code, name: c.name, subId: s.id, subName: s.name })
        }
      } else {
        cur.items.push({ value: `${c.code}|`, label: c.name,
          code: c.code, name: c.name, subId: null, subName: null })
      }
    }
    return groups
  }, [cats, subsByCode])

  // value 로 되찾기 위한 평평한 표
  const catByValue = useMemo(() => {
    const m = new Map()
    for (const g of catGroups) for (const it of g.items) m.set(it.value, it)
    return m
  }, [catGroups])

  // 부서는 사업부(에르모어/링크맘)로 묶어 보여준다 - 이름만 스무 개면 못 고른다
  const deptGroups = useMemo(() => {
    const m = new Map()
    for (const d of depts) {
      const label = d.buName || '기타'
      if (!m.has(label)) m.set(label, { label, items: [] })
      m.get(label).items.push(d)
    }
    return [...m.values()]
  }, [depts])

  const filtered = useMemo(() => {
    if (!rows) return null
    let list = rows
    // 전체계좌 조회이거나 캐시가 아직 옛 형식일 때를 대비해 화면에서도 한 번 더 가른다.
    // 외화여부가 없는(재동기화 전) 레코드는 원화로 취급한다.
    if (hasCcyTabs) {
      // 계좌는 외화계좌 여부로, 증권은 통화로 가른다
      const isFx = (r) => (kind === 'bank' ? !!r['외화여부'] : (r['통화'] || 'KRW') !== 'KRW')
      list = list.filter((r) => (ccyTab === 'fx' ? isFx(r) : !isFx(r)))
    }
    if (hasAccount && acctFilter) list = list.filter((r) => r['우리계좌'] === acctFilter)
    if (hasFlow && flowFilter === '입금') list = list.filter((r) => Number(r['입금액']) > 0)
    if (hasFlow && flowFilter === '출금') list = list.filter((r) => Number(r['출금액']) > 0)
    if (catPick.size) list = list.filter((r) => catPick.has(r[CAT_NAME[kind]]))
    if (deptPick.size) list = list.filter((r) => deptPick.has(r['부서명']))
    if (vendorPick.size) list = list.filter((r) => vendorPick.has(r['위하고거래처명']))
    if (quick === 'cat') list = list.filter((r) => !r[CAT_NAME[kind]])
    if (quick === 'vendor') list = list.filter((r) => !r['위하고거래처명'])
    if (quick === 'dept') list = list.filter((r) => !r['부서명'])
    const needle = q.trim().toLowerCase()
    if (!needle) return list
    return list.filter((r) => cfg.cols.some((c) => String(r[c.k] ?? '').toLowerCase().includes(needle)))
  }, [rows, q, cfg, hasAccount, hasFlow, acctFilter, flowFilter, hasCcyTabs, kind, ccyTab,
      catPick, deptPick, vendorPick, quick])

  // 감춘 열을 뺀 실제 표 열. 합계·머리·본문이 모두 이걸 쓴다.
  const 보일열 = useMemo(() => cfg.cols.filter((c) => !hidden.has(c.k)), [cfg, hidden])
  const 원천수 = 보일열.filter((c) => !분류열.has(c.k)).length
  const 분류수 = 보일열.length - 원천수
  const 열토글 = (k) => {
    const n = new Set(hidden)
    if (n.has(k)) n.delete(k); else n.add(k)
    setHidden(n)
    기억.set(`cols-${kind}`, JSON.stringify([...n]))
  }
  const 기간고르기 = (k) => { const [a, b] = 기간계산(k); setStart(a); setEnd(b) }

  const totals = useMemo(() => {
    if (!filtered) return {}
    const mixedCcy = new Set(filtered.map((r) => r['통화']).filter(Boolean)).size > 1
    const t = {}
    for (const c of cfg.cols) {
      if (!c.num || c.k === '잔액' || c.noSum) continue
      if (c.fx && mixedCcy) continue // USD+CNY를 더한 숫자는 뜻이 없다
      t[c.k] = filtered.reduce((s, r) => s + (Number(r[c.k]) || 0), 0)
    }
    return t
  }, [filtered, cfg])

  // 위쪽 조작줄과 표 머리를 고정하고 행만 스크롤시킨다.
  // 화면 영역(.content)을 창 높이에 맞춘 세로 flex로 바꾸고 표 패널이 남은 높이를 다 먹게
  // 하면, 문서 전체가 아니라 패널 안에서만 스크롤이 생겨 sticky가 제대로 걸린다.
  // (패널에 높이가 없으면 세로로 스크롤될 일이 없어 sticky가 아무것도 하지 않는다.)
  const panelRef = useRef(null)
  const headRowRef = useRef(null)
  const grpRowRef = useRef(null)
  useEffect(() => {
    const content = panelRef.current?.closest('.content')
    if (!content) return
    content.classList.add('fit-viewport')
    return () => content.classList.remove('fit-viewport')
  }, [filtered])

  // 머리가 세 겹이다 — 영역줄 · 열이름줄 · 합계줄. 각자 위 줄들의 높이만큼 내려서
  // 고정해야 스크롤할 때 겹치지 않는다. 글자 크기나 열 이름 길이에 따라 높이가
  // 달라지므로 숫자를 박지 않고 재서 넘긴다.
  useEffect(() => {
    const el = panelRef.current
    const row = headRowRef.current
    if (!el || !row) return
    const measure = () => {
      el.style.setProperty('--grp-h', `${grpRowRef.current?.offsetHeight ?? 0}px`)
      el.style.setProperty('--head-h', `${row.offsetHeight}px`)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(row)
    if (grpRowRef.current) ro.observe(grpRowRef.current)
    return () => ro.disconnect()
  }, [filtered, cfg, 보일열])

  const stage = (tid, patch) => setEditRows((p) => ({ ...p, [tid]: { ...p[tid], ...patch } }))

  const saveEdits = async () => {
    const items = Object.entries(editRows).map(([tid, e]) => {
      const it = { ticketId: Number(tid) }
      if (e.desc !== undefined) it.description = e.desc
      if (e.catId !== undefined) { it.expenseCategoryId = e.catId; it.expenseCategoryName = e.catName }
      if (e.subitemId !== undefined) { it.subitemId = e.subitemId; it.subitemName = e.subitemName }
      if (e.deptCode !== undefined) { it.departmentCode = e.deptCode; it.departmentName = e.deptName }
      if (e.fundItemId !== undefined) it.fundItemId = e.fundItemId
      return it
    }).filter((it) => Object.keys(it).length > 1)
    if (!items.length) return
    try {
      await portal.post(`/api/fa/${kind}/update`, { items })
      notify(`${items.length}건 저장 완료`)
      load()
    } catch (e) { setErr(e.message) }
  }

  // 엑셀처럼 shift+클릭으로 구간 선택. 직전에 누른 행을 기억해 두고, shift를 누른 채 다른
  // 행을 누르면 그 사이를 전부 같은 상태로 맞춘다(잠긴 행은 건너뛴다).
  const lastClicked = useRef(null)
  useEffect(() => { lastClicked.current = null }, [kind, ccyTab, start, end, acctFilter, flowFilter, q])

  const clickSel = (e, idx, tid) => {
    const check = !sel.has(tid)
    const from = lastClicked.current
    setSel((prev) => {
      const next = new Set(prev)
      const range = e.shiftKey && from !== null && from !== idx
        ? Array.from({ length: Math.abs(idx - from) + 1 }, (_, k) => Math.min(from, idx) + k)
        : [idx]
      for (const i of range) {
        const row = filtered[i]
        if (!row || !editable(row)) continue
        check ? next.add(row.ticket_id) : next.delete(row.ticket_id)
      }
      return next
    })
    lastClicked.current = idx
    if (e.shiftKey) window.getSelection()?.removeAllRanges()  // shift 드래그로 잡힌 텍스트 해제
  }

  const submitVoucher = async () => {
    if (!sel.size || !approverId) return
    const picked = filtered.filter((r) => sel.has(r.ticket_id))
    // 승인 대상은 계정과목 한 칸이 아니라 분개다 - 계정과목이나 거래처가 비면 상대 계정이
    // 없는 분개가 그대로 확정되므로, 넘어가기 전에 한 번 세워 준다.
    const noCat = picked.filter((r) => !r[CAT_NAME[kind]]).length
    const noVendor = picked.filter((r) => !r['위하고거래처코드']).length
    if (noCat || noVendor) {
      const parts = []
      if (noCat) parts.push(`계정과목 미지정 ${noCat}건`)
      if (noVendor) parts.push(`거래처 미매칭 ${noVendor}건`)
      if (!confirm(`${parts.join(' / ')}이 포함돼 있습니다.
이대로 승인요청하면 그 자리가 빈 분개로 확정됩니다. 계속할까요?`)) return
    }
    const items = picked.map((r) => ({
      ticket_id: r.ticket_id,
      date: r['일자'],
      counterparty: r[cfg.cpField] || '',
      deposit: r['입금액'] ?? null,
      withdraw: r['출금액'] ?? null,
      amount: r['합계'] ?? null,
      our_account: r['우리계좌'] ?? null,
      prev_description: r['적요'] ?? null,
      prev_category_id: r[CAT_ID[kind]] ?? null,
      prev_category_name: r[CAT_NAME[kind]] || null,
    }))
    try {
      await portal.post('/api/voucher/requests', {
        kind, type: 'submit', approverId: Number(approverId), memo: memo || null, items,
      })
      notify(`${items.length}건 승인요청 완료`)
      setMemo('')
      load()
    } catch (e) { setErr(e.message) }
  }

  const editable = (r) => !isLocked(r)

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{cfg.title}</h2>
        <NoteDrawer target={`d-${kind}`} screenKey={SCREEN_KEY[kind]}
          context={{ start, end }} contextLabel={`${start}~${end}`}
          onNavigate={(ctx) => { if (ctx?.start) setStart(ctx.start); if (ctx?.end) setEnd(ctx.end) }}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {!isBank && hasCcyTabs && (
        <div className="data-tabs">
          {[{ k: 'krw', label: '원화' }, { k: 'fx', label: '외화' }].map((t) => (
            <button key={t.k} type="button"
              className={'data-tab' + (ccyTab === t.k ? ' active' : '')}
              onClick={() => changeCcyTab(t.k)}>{t.label}</button>
          ))}
        </div>
      )}
      {isBank && (
        <>
          <div className="data-tabs">
            {[{ k: 'krw', label: '원화' }, { k: 'fx', label: '외화' }].map((t) => (
              <button key={t.k} type="button"
                className={'data-tab' + (ccyTab === t.k ? ' active' : '')}
                onClick={() => changeCcyTab(t.k)}>{t.label}</button>
            ))}
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted">계좌</span>
            <select value={selectedAsset ?? ''} onChange={(e) => changeAccount(e.target.value)} style={{ maxWidth: 240 }} disabled={!visibleAccounts}
              title="전체계좌는 한 달치를 통째로 끌어와 느립니다. 다시 열면 계좌 하나로 돌아옵니다.">
              {!visibleAccounts && <option>불러오는 중…</option>}
              {visibleAccounts && <option value="ALL">{ccyTab === 'fx' ? '전체 외화계좌' : '전체계좌 (느릴 수 있음)'}</option>}
              {visibleAccounts?.map((a) => <option key={a.assetId} value={a.assetId}>{a.label}</option>)}
            </select>
            <span className="muted" style={{ fontSize: 12 }}>
              {ccyTab === 'fx'
                ? '외화계좌는 통화별로 나뉩니다. 환율은 수집된 은행 환산가라 매매기준율과 다를 수 있습니다.'
                : '계정과목·거래처는 계좌별로 정리하니, 기본은 계좌 1개만 불러옵니다.'}
            </span>
          </div>
          {visibleAccounts && visibleAccounts.length === 0 && (
            <div className="muted" style={{ marginBottom: 10, fontSize: 12 }}>
              {ccyTab === 'fx' ? '외화계좌가 아직 인식되지 않았습니다 — [동기화]를 한 번 실행해 주세요.' : ''}
            </div>
          )}
        </>
      )}
      {/* 기간 — 자주 쓰는 단위를 단추로 두고, 그 밖의 기간은 옆에서 직접 고른다 */}
      <div className="row toolbar" style={{ marginBottom: 8 }}>
        <div className="seg-btns">
          {기간버튼.map((b) => (
            <button key={b.k} type="button" onClick={() => 기간고르기(b.k)}>{b.label}</button>
          ))}
        </div>
        <DateRange start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e) }} />
        <span style={{ marginLeft: 'auto' }} />
        {isCached && (
          <>
            <button className="btn secondary" onClick={doSync} disabled={syncing}>
              {syncing ? '동기화 중…' : '동기화'}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              {syncing ? '최신 데이터를 받는 중' :
                lastSynced ? `마지막 동기화 ${String(lastSynced).slice(5, 16).replace('T', ' ')}` : '동기화 기록 없음'}
            </span>
          </>
        )}
      </div>

      {/* 손봐야 할 자리로 바로 가는 단추. 숫자가 붙어 있어 남은 일이 한눈에 보인다 */}
      <div className="row chips" style={{ marginBottom: 8 }}>
        <button type="button" className={'chip' + (quick ? '' : ' on')} onClick={() => setQuick('')}>
          전체{rows ? <span className="chip-n">{fmt(rows.length)}</span> : null}
        </button>
        <button type="button" className={'chip' + (quick === 'cat' ? ' on' : '')}
          onClick={() => setQuick(quick === 'cat' ? '' : 'cat')}>
          계정 없음{빈칸수.cat ? <span className="chip-n">{fmt(빈칸수.cat)}</span> : null}
        </button>
        <button type="button" className={'chip' + (quick === 'vendor' ? ' on' : '')}
          onClick={() => setQuick(quick === 'vendor' ? '' : 'vendor')}>
          거래처 없음{빈칸수.vendor ? <span className="chip-n">{fmt(빈칸수.vendor)}</span> : null}
        </button>
        {cfg.hasDept && (
          <button type="button" className={'chip' + (quick === 'dept' ? ' on' : '')}
            onClick={() => setQuick(quick === 'dept' ? '' : 'dept')}>
            부서 없음{빈칸수.dept ? <span className="chip-n">{fmt(빈칸수.dept)}</span> : null}
          </button>
        )}
      </div>

      <div className="row toolbar" style={{ marginBottom: 10 }}>
        <input className="search" placeholder={isBank ? '적요, 거래자명, 금액 검색' : '내용, 거래처, 금액 검색'}
          value={q} onChange={(e) => setQ(e.target.value)} />
        {hasFlow && (
          <div className="seg-btns">
            {[['', '입출금'], ['입금', '입금'], ['출금', '출금']].map(([v, l]) => (
              <button key={l} type="button" className={flowFilter === v ? 'on' : ''}
                onClick={() => setFlowFilter(v)}>{l}</button>
            ))}
          </div>
        )}
        {hasAccount && !isBank && (
          <select value={acctFilter} onChange={(e) => setAcctFilter(e.target.value)} style={{ maxWidth: 200 }}>
            <option value="">전체 계좌</option>
            {accountOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <FilterPopover label="계정" options={후보.cat} value={catPick} onChange={setCatPick} />
        <FilterPopover label="거래처" options={후보.vendor} value={vendorPick} onChange={setVendorPick} />
        {cfg.hasDept && (
          <FilterPopover label="부서" options={후보.dept} value={deptPick} onChange={setDeptPick} />
        )}
        {filtered && <span className="muted">{fmt(filtered.length)}건</span>}
        <span style={{ marginLeft: 'auto' }} />
        {/* 열 고르기 — 계좌 화면은 열이 열다섯이 넘어 사람마다 보고 싶은 것이 다르다 */}
        <div className="fpop">
          <button type="button" className={'fpop-btn' + (hidden.size ? ' on' : '')}
            onClick={() => setColsOpen(!colsOpen)}>
            표시 설정{hidden.size ? <span className="fpop-n">{cfg.cols.length - hidden.size}</span> : null}
            <span className="fpop-caret">▾</span>
          </button>
          {colsOpen && (
            <div className="fpop-panel" style={{ width: 220, right: 0, left: 'auto' }}>
              <div className="fpop-list">
                {cfg.cols.map((c) => (
                  <label key={c.k} className="fpop-item">
                    <input type="checkbox" checked={!hidden.has(c.k)} onChange={() => 열토글(c.k)} />
                    <span className="fpop-name">{c.label ?? c.k}</span>
                    <span className="muted fpop-sub">{분류열.has(c.k) ? '분류' : '원천'}</span>
                  </label>
                ))}
              </div>
              <div className="fpop-foot">
                <button type="button" className="btn secondary tiny"
                  onClick={() => { setHidden(new Set()); 기억.set(`cols-${kind}`, '[]') }}>모두 보기</button>
                <button type="button" className="btn secondary tiny"
                  onClick={() => setColsOpen(false)}>닫기</button>
              </div>
            </div>
          )}
        </div>
        <button className="btn" onClick={saveEdits} disabled={!Object.keys(editRows).length}>
          수정 저장 ({Object.keys(editRows).length})
        </button>
      </div>
      {sel.size > 0 && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="muted">선택 {sel.size}건 일괄변경:</span>
          {cfg.hasDesc && (
            <>
              <input placeholder="적요 일괄 입력" style={{ width: 180 }} value={bulkDesc}
                onChange={(e) => setBulkDesc(e.target.value)} />
              <button className="btn secondary" disabled={!bulkDesc}
                onClick={() => {
                  const targets = filtered.filter((r) => sel.has(r.ticket_id) && editable(r))
                  targets.forEach((r) => stage(r.ticket_id, { desc: bulkDesc }))
                  notify(`적요 ${targets.length}건 반영 대기 (수정 저장을 눌러주세요)`)
                }}>적요 일괄적용</button>
            </>
          )}
          <select value={bulkCatId} onChange={(e) => setBulkCatId(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="">계정·세목 선택</option>
            {catGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.items.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </optgroup>
            ))}
          </select>
          <button className="btn secondary" disabled={!bulkCatId}
            onClick={() => {
              const x = catByValue.get(bulkCatId)
              const targets = filtered.filter((r) => sel.has(r.ticket_id) && editable(r))
              targets.forEach((r) => stage(r.ticket_id, {
                catId: x ? x.code : null, catName: x ? x.name : null,
                subitemId: x ? x.subId : null, subitemName: x ? x.subName : null,
              }))
              notify(`${x ? x.label : '계정과목'} ${targets.length}건 반영 대기 (수정 저장을 눌러주세요)`)
            }}>일괄적용</button>
          {cfg.hasDept && (
            <>
              <select value={bulkDeptCode} onChange={(e) => setBulkDeptCode(e.target.value)} style={{ maxWidth: 170 }}>
                <option value="">부서 선택</option>
                {deptGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <button className="btn secondary" disabled={!bulkDeptCode}
                onClick={() => {
                  const d = depts.find((x) => x.code === bulkDeptCode)
                  const targets = filtered.filter((r) => sel.has(r.ticket_id) && editable(r))
                  targets.forEach((r) => stage(r.ticket_id, { deptCode: d ? d.code : null, deptName: d ? d.name : null }))
                  notify(`부서 ${targets.length}건 반영 대기 (수정 저장을 눌러주세요)`)
                }}>부서 일괄적용</button>
            </>
          )}
          {funds.length > 0 && (
            <>
              <select value={bulkFundId} onChange={(e) => setBulkFundId(e.target.value)} style={{ maxWidth: 170 }}>
                <option value="">자금항목 선택</option>
                <optgroup label="수입">
                  {funds.filter((f) => f['구분'] === '수입').map((f) => <option key={f.id} value={f.id}>{f['항목명']}</option>)}
                </optgroup>
                <optgroup label="지출">
                  {funds.filter((f) => f['구분'] === '지출').map((f) => <option key={f.id} value={f.id}>{f['항목명']}</option>)}
                </optgroup>
              </select>
              <button className="btn secondary" disabled={!bulkFundId}
                onClick={() => {
                  const targets = filtered.filter((r) => sel.has(r.ticket_id) && editable(r))
                  targets.forEach((r) => stage(r.ticket_id, { fundItemId: Number(bulkFundId) }))
                  notify(`자금항목 ${targets.length}건 반영 대기 (수정 저장을 눌러주세요)`)
                }}>자금항목 일괄적용</button>
            </>
          )}
          {/* 거래처는 저장 대기 없이 바로 반영된다 - 셀에서 고칠 때와 같은 방식이다 */}
          <button className="btn secondary" onClick={() => setBulkVendorOpen(true)}>
            {bulkVendor ? `거래처: ${bulkVendor.name}` : '위하고 거래처 선택'}
          </button>
          <button className="btn secondary" disabled={!bulkVendor}
            onClick={async () => {
              const targets = filtered.filter((r) => sel.has(r.ticket_id) && editable(r))
              if (!targets.length) return
              try {
                const res = await portal.post('/api/vendor-match/bulk', {
                  kind, ticketIds: targets.map((r) => r.ticket_id),
                  vendorCode: bulkVendor.code, vendorName: bulkVendor.name,
                  vendorKind: bulkVendor.kind || 'general',
                })
                notify(`거래처 ${res.updated}건 적용${res.locked ? ` · 잠긴 ${res.locked}건 제외` : ''}`)
                load()
              } catch (e) { setErr(e.message) }
            }}>거래처 일괄적용</button>
        </div>
      )}
      {bulkVendorOpen && (
        <VendorPickerModal onClose={() => setBulkVendorOpen(false)}
          onSelect={(v) => { setBulkVendor(v); setBulkVendorOpen(false) }} />
      )}
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="muted">선택 {sel.size}건 승인요청 →</span>
        <select value={approverId} onChange={(e) => setApproverId(e.target.value)}>
          <option value="">담당자 선택</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input placeholder="메모(선택)" style={{ width: 200 }} value={memo} onChange={(e) => setMemo(e.target.value)} />
        <button className="btn" onClick={submitVoucher} disabled={!sel.size || !approverId}>승인요청</button>
      </div>
      {err && <div className="error-box">{err}</div>}
      {!err && (loading || !filtered) && <div className="muted">불러오는 중…</div>}
      {!err && filtered && (
        <div className="panel table-frozen" ref={panelRef}>
          <table>
            <thead>
              {/* 어디까지가 은행이 준 것이고 어디부터가 우리가 붙인 것인지 갈라 놓는다 */}
              <tr className="grp-head" ref={grpRowRef}>
                <th />
                {원천수 > 0 && <th colSpan={원천수}>{KIND_TITLE[kind] ?? '원천'} 내역</th>}
                {분류수 > 0 && <th colSpan={분류수} className="g-cls">분류 내역</th>}
              </tr>
              <tr ref={headRowRef}>
                <th style={{ width: 28 }}>
                  <input type="checkbox"
                    checked={filtered.some((r) => editable(r)) && filtered.filter(editable).every((r) => sel.has(r.ticket_id))}
                    onChange={(e) => setSel(e.target.checked ? new Set(filtered.filter(editable).map((r) => r.ticket_id)) : new Set())} />
                </th>
                {보일열.map((c) => (
                  <th key={c.k} className={분류열.has(c.k) ? 'g-cls' : undefined}>{c.label ?? c.k}</th>
                ))}
              </tr>
              {filtered.length > 0 && (
                <tr className="total">
                  <td />
                  {보일열.map((c, i) => (
                    <td key={c.k} className={c.num ? '' : 'l'}>
                      {i === 0 ? '합계' : c.num && totals[c.k] !== undefined ? fmt(totals[c.k], c.dec || 0, c.dec || 0) : ''}
                    </td>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={보일열.length + 1} className="c muted">해당 기간 내역이 없습니다</td></tr>}
              {filtered.map((r, i) => {
                const tid = r.ticket_id
                const ed = editRows[tid] || {}
                return (
                  <tr key={tid ?? i}>
                    <td className="c">
                      <input type="checkbox" disabled={!editable(r)}
                        checked={sel.has(tid)} readOnly
                        onClick={(e) => clickSel(e, i, tid)} />
                    </td>
                    {보일열.map((c) => {
                      if (c.bank) {
                        return <td key={c.k} className="c">{bankShort(r['계좌은행'])}</td>
                      }
                      if (c.acct) {
                        return <td key={c.k} className="c" title={r['우리계좌']}>{acctLast4(r['계좌번호'])}</td>
                      }
                      if (c.journal) {
                        // 대부분은 계정과목 하나로 끝나지만, 외화 송금 수수료나 선급금/외상매입금
                        // 분할처럼 쪼개야 하는 건은 여기서 차·대를 직접 짠다.
                        // 평소엔 열지 않아도 되도록, 분개가 설 수 있는 상태인지만 표시한다.
                        const ready = !!r[CAT_NAME[kind]] && !!r['위하고거래처코드']
                        return (
                          <td key={c.k} className="c">
                            <button type="button" className="link-cell"
                              style={ready ? undefined : { color: 'var(--danger)' }}
                              title={ready ? '분개 보기 · 쪼개기' : '계정과목/거래처를 지정하면 분개가 자동으로 섭니다'}
                              onClick={() => setJournalFor(tid)}>
                              {ready ? '분개' : '미완'}
                            </button>
                          </td>
                        )
                      }
                      if (c.vendor) {
                        return <td key={c.k} className="l"><VendorCell row={r} kind={kind} cpField={cfg.cpField} onSaved={load} /></td>
                      }
                      if (c.cat && editable(r)) {
                        const code = ed.catId !== undefined ? ed.catId : r[CAT_ID[kind]]
                        const subId = ed.subitemId !== undefined ? ed.subitemId : r['세목ID']
                        const cur = code ? `${code}|${subId ?? ''}` : ''
                        const 고른것 = catByValue.get(cur)
                        return (
                          <td key={c.k} className="l">
                            <select value={cur} style={{ maxWidth: 200 }}
                              onChange={(e) => {
                                const x = catByValue.get(e.target.value)
                                stage(tid, {
                                  catId: x ? x.code : null, catName: x ? x.name : null,
                                  subitemId: x ? x.subId : null, subitemName: x ? x.subName : null,
                                })
                              }}>
                              <option value="">미지정</option>
                              {catGroups.map((g) => (
                                <optgroup key={g.label} label={g.label}>
                                  {g.items.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                                </optgroup>
                              ))}
                            </select>
                            {고른것?.subName && (
                              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                                {고른것.name}
                              </div>
                            )}
                          </td>
                        )
                      }
                      // 잠긴 건은 고칠 수 없지만 무엇으로 잡혔는지는 보여야 한다
                      if (c.cat) {
                        const 세 = r['세목']
                        return (
                          <td key={c.k} className="l">
                            {세 ? <>{세}<div className="muted" style={{ fontSize: 11 }}>{r[CAT_NAME[kind]]}</div></>
                                : (r[CAT_NAME[kind]] || <span className="muted">—</span>)}
                          </td>
                        )
                      }
                      if (c.fund && editable(r)) {
                        const cur = ed.fundItemId !== undefined ? ed.fundItemId : (r['자금항목ID'] ?? '')
                        return (
                          <td key={c.k} className="l">
                            <select value={cur ?? ''} style={{ maxWidth: 140 }}
                              title="자금계획의 항목입니다. 계정과목이 회계 분류라면 이건 자금 분류입니다 — 외상매입금으로 나갔어도 자금으로는 상품대인지 수수료인지가 다릅니다."
                              onChange={(e) => stage(tid, { fundItemId: e.target.value ? Number(e.target.value) : null })}>
                              <option value="">규칙에 맡김</option>
                              <optgroup label="수입">
                                {funds.filter((f) => f['구분'] === '수입')
                                  .map((f) => <option key={f.id} value={f.id}>{f['항목명']}</option>)}
                              </optgroup>
                              <optgroup label="지출">
                                {funds.filter((f) => f['구분'] === '지출')
                                  .map((f) => <option key={f.id} value={f.id}>{f['항목명']}</option>)}
                              </optgroup>
                            </select>
                          </td>
                        )
                      }
                      if (c.dept && editable(r)) {
                        const cur = ed.deptCode !== undefined ? ed.deptCode : (r['부서코드'] ?? '')
                        return (
                          <td key={c.k} className="l">
                            <select value={cur ?? ''} style={{ maxWidth: 150 }}
                              onChange={(e) => {
                                const d = depts.find((x) => x.code === e.target.value)
                                stage(tid, { deptCode: d ? d.code : null, deptName: d ? d.name : null })
                              }}>
                              <option value="">미지정</option>
                              {deptGroups.map((g) => (
                                <optgroup key={g.label} label={g.label}>
                                  {g.items.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
                                </optgroup>
                              ))}
                            </select>
                          </td>
                        )
                      }
                      if (c.desc && cfg.hasDesc && editable(r)) {
                        return (
                          <td key={c.k} className="l">
                            <input className="wide" value={ed.desc !== undefined ? ed.desc : (r['적요'] ?? '')}
                              onChange={(e) => stage(tid, { desc: e.target.value })} />
                          </td>
                        )
                      }
                      let v = r[c.k]
                      if (c.badge) v = STATUS_LABEL[v] ?? v ?? ''
                      return (
                        <td key={c.k} className={c.align || (c.num ? '' : 'l')}>
                          {c.num ? (v ? fmt(v, c.dec || 0, c.dec || 0) : '') : (v ?? '')}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {journalFor != null && (
        <JournalEditor kind={kind} ticketId={journalFor}
          onClose={() => setJournalFor(null)} onSaved={() => notify('분개를 저장했습니다')} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
